<template>
  <div class="technology-page">
    <!-- 导航栏 -->
    <WebsiteNavbar />

    <div class="hero-section">
      <div class="hero-background">
        <img :src="heroBackgroundImage" alt="Technology Background" />
        <div class="slide-overlay"></div>
      </div>
      <div class="container">
        <h1 class="page-title">{{ t('technology.hero.title') }}</h1>
        <p class="page-subtitle">{{ t('technology.hero.subtitle') }}</p>
      </div>
    </div>
    
    <div class="content-section">
      <div class="container">
        <div class="tech-grid">
          <div
            class="tech-card"
            v-for="tech in technologies"
            :key="tech.id"
            :style="{ backgroundImage: `url(${tech.backgroundImage})` }"
          >
            <div class="card-overlay"></div>
            <div class="card-content">
              <h3 class="tech-title">{{ t(tech.titleKey) }}</h3>
              <p class="tech-description">{{ t(tech.descriptionKey) }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 页脚 -->
    <WebsiteFooter />
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import WebsiteNavbar from './components/WebsiteNavbar.vue'
import WebsiteFooter from './components/WebsiteFooter.vue'

const { t } = useI18n()
const heroBackgroundImage = new URL('@/assets/carousel/slide1.webp', import.meta.url).href

interface Technology {
  id: number
  titleKey: string
  descriptionKey: string
  backgroundImage: string
}

const technologies = ref<Technology[]>([
  {
    id: 1,
    titleKey: 'technology.items.smartManufacturing.title',
    descriptionKey: 'technology.items.smartManufacturing.description',
    backgroundImage: new URL('@/assets/products-center/Machine Parts.png', import.meta.url).href
  },
  {
    id: 2,
    titleKey: 'technology.items.precisionControl.title',
    descriptionKey: 'technology.items.precisionControl.description',
    backgroundImage: new URL('@/assets/products-center/Pneumatic Controllers.png', import.meta.url).href
  },
  {
    id: 3,
    titleKey: 'technology.items.energySaving.title',
    descriptionKey: 'technology.items.energySaving.description',
    backgroundImage: new URL('@/assets/products-center/E-Motion products.png', import.meta.url).href
  },
  {
    id: 4,
    titleKey: 'technology.items.digitalTransformation.title',
    descriptionKey: 'technology.items.digitalTransformation.description',
    backgroundImage: new URL('@/assets/products-center/Customerized Solutions.png', import.meta.url).href
  }
])
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Bitter:wght@300;400;500;600;700&family=Hind:wght@300;400;500;600;700&display=swap');

.technology-page {
  font-family: 'Hind', 'Bitter', 'Helvetica Neue', Helvetica, Arial, sans-serif;
  color: #333;
  overflow-x: hidden;
}

.hero-section {
  position: relative;
  color: white;
  padding: 120px 0 80px;
  text-align: center;
  margin-top: 75px;
}

.hero-background {
  position: absolute;
  inset: 0;
  z-index: -1;
}

.hero-background img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.slide-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to right, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.4));
}

.container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 50px;
}

.page-title {
  font-size: 58px;
  font-weight: 700;
  margin-bottom: 28px;
  line-height: 1.2;
  text-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
  font-family: 'Bitter', serif;
  letter-spacing: -0.5px;
}

.page-subtitle {
  font-size: 20px;
  margin-bottom: 45px;
  opacity: 0.95;
  font-weight: 300;
  line-height: 1.7;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
  font-family: 'Hind', sans-serif;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
}

.content-section {
  padding: 90px 0;
  background-color: #f5f6f8;
}

.tech-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 32px;
}

.tech-card {
  position: relative;
  height: 340px;
  border-radius: 20px;
  overflow: hidden;
  cursor: pointer;
  background-size: cover;
  background-position: center;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
}

.tech-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.2);
}

.card-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.45), rgba(0, 0, 0, 0.75));
  transition: all 0.4s ease;
}

.tech-card:hover .card-overlay {
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.55), rgba(0, 0, 0, 0.85));
}

.card-content {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  padding: 35px;
  text-align: center;
}

.tech-title {
  color: white;
  font-size: 24px;
  font-weight: 700;
  text-align: center;
  text-shadow: 0 3px 12px rgba(0, 0, 0, 0.4);
  line-height: 1.4;
  font-family: 'Bitter', serif;
  letter-spacing: 0.2px;
  margin-bottom: 15px;
  transition: transform 0.3s;
}

.tech-card:hover .tech-title {
  transform: translateY(-3px);
}

.tech-description {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.6;
  font-weight: 300;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  font-family: 'Hind', sans-serif;
}

@media (max-width: 1024px) {
  .tech-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: 28px;
  }

  .container {
    padding: 0 30px;
  }
}

@media (max-width: 768px) {
  .container {
    padding: 0 20px;
  }

  .hero-section {
    padding: 100px 0 60px;
    margin-top: 65px;
  }

  .content-section {
    padding: 70px 0;
  }

  .tech-grid {
    grid-template-columns: 1fr;
    gap: 24px;
  }

  .tech-card {
    height: 300px;
  }

  .page-title {
    font-size: 38px;
  }

  .page-subtitle {
    font-size: 17px;
    margin-bottom: 35px;
  }

  .tech-title {
    font-size: 22px;
  }
}
</style>